import React from "react";
import { osc, orbitAngle, wobble, stagger } from "../../core/AnimationUtils";

/* ═══════════════════════════════════════════════════════════════
   Space & Astronomy — 8 templates
   rocket_launch, satellite_orbit, galaxy_spiral, moon_phases,
   constellation_map, star_lifecycle, telescope_view, asteroid_belt
   ═══════════════════════════════════════════════════════════════ */

/* ─── 1. Rocket Launch ────────────────────────────────────── */
export interface RocketLaunchProps {
  label?: string;
  animationProgress?: number;
  accentColor?: string;
  /** Seconds since scene started — drives exhaust flicker, star twinkle, smoke drift. */
  t?: number;
}
export const RocketLaunch: React.FC<RocketLaunchProps> = (props) => {
  const label = props.label || "Rocket Launch";
  const animationProgress = props.animationProgress || 0;
  const accentColor = props.accentColor || "#e74c3c";
  const t = props.t ?? 0;

  const rocketY = 400 - animationProgress * 300;

  // Rocket body sway increases slightly during flight
  const sway = wobble(t, 2.8, 2.5);
  // Exhaust flame flicker: fast oscillation
  const flameScale = osc(t, 0.18, 0.8, 1.4);
  // Star twinkle phases
  const starPhases = Array.from({ length: 40 }, (_, i) => stagger(i, 0.25));

  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <linearGradient id="rl-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0a0a2e" />
          <stop offset={`${60 + animationProgress * 40}%`} stopColor="#1a237e" />
          <stop offset="100%" stopColor="#283593" />
        </linearGradient>
        <radialGradient id="rl-flame" cx="50%" cy="0%" r="80%">
          <stop offset="0%" stopColor="#fff" /><stop offset="30%" stopColor="#ff9800" />
          <stop offset="100%" stopColor={accentColor} stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="800" height="600" fill="url(#rl-sky)" rx="16" />
      {/* Stars — twinkling via t */}
      {Array.from({ length: 40 }, (_, i) => {
        const baseOpacity = 0.3 + (i % 4) * 0.2;
        const twinkle = osc(t, 2.5 + (i % 5) * 0.4, baseOpacity * 0.5, baseOpacity, starPhases[i]);
        return (
          <circle key={i} cx={50 + (i * 197) % 700} cy={30 + (i * 131) % 560}
            r={0.5 + (i % 3) * 0.5} fill="#fff" opacity={twinkle} />
        );
      })}
      {/* Ground */}
      <rect x="0" y="520" width="800" height="80" fill="#1b5e20" rx="0" />
      {/* Launch pad */}
      <rect x="360" y="510" width="80" height="15" rx="3" fill="#546e7a" />
      {/* Rocket */}
      <g transform={`translate(${400 + sway}, ${rocketY})`}>
        {/* Flame (flickers with t) */}
        {animationProgress > 0 && (
          <ellipse cx="0" cy={60 + animationProgress * 20}
            rx={(15 + animationProgress * 10) * flameScale}
            ry={(30 + animationProgress * 30) * flameScale}
            fill="url(#rl-flame)" opacity="0.85" />
        )}
        {/* Body */}
        <rect x="-16" y="-40" width="32" height="80" rx="4" fill="#ecf0f1" />
        {/* Nose cone */}
        <polygon points="-16,-40 0,-80 16,-40" fill={accentColor} />
        {/* Window */}
        <circle cx="0" cy="-10" r="8" fill="#1a237e" stroke="#bdbdbd" strokeWidth="1.5" />
        {/* Fins */}
        <polygon points="-16,30 -30,50 -16,40" fill={accentColor} />
        <polygon points="16,30 30,50 16,40" fill={accentColor} />
      </g>
      {/* Smoke trail — puffs drift with t */}
      {animationProgress > 0.1 && (
        <g opacity={animationProgress * 0.5}>
          {[0, 1, 2, 3, 4].map((i) => {
            const driftX = wobble(t, 3.5 + i * 0.4, 5 + i * 3, i);
            return (
              <ellipse key={i} cx={400 + driftX + (i % 2 === 0 ? -1 : 1) * i * 8}
                cy={rocketY + 80 + i * 40} rx={10 + i * 8} ry={8 + i * 5}
                fill="#bdbdbd" opacity={0.3 - i * 0.05} />
            );
          })}
        </g>
      )}
      <text x="400" y="580" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 2. Satellite Orbit ──────────────────────────────────── */
export interface SatelliteOrbitProps {
  label?: string;
  rotation?: number;
  accentColor?: string;
  /** Seconds since scene started — drives continuous satellite orbit and signal pulse. */
  t?: number;
}
export const SatelliteOrbit: React.FC<SatelliteOrbitProps> = (props) => {
  const label = props.label || "Satellite Orbit";
  const accentColor = props.accentColor || "#3498db";
  const t = props.t ?? 0;

  // Satellite orbits continuously: one full orbit every 7 seconds
  const satAngle = orbitAngle(t, 7.0, Math.PI * 0.1);
  // Orbit is tilted ellipse: rx=220, ry=80, tilt=-20 deg
  const tiltRad = (-20 * Math.PI) / 180;
  const orbRx = 220, orbRy = 80;
  const satX = 400 + orbRx * Math.cos(satAngle) * Math.cos(tiltRad) - orbRy * Math.sin(satAngle) * Math.sin(tiltRad);
  const satY = 300 + orbRx * Math.cos(satAngle) * Math.sin(tiltRad) + orbRy * Math.sin(satAngle) * Math.cos(tiltRad);
  // Satellite facing angle follows its path tangent
  const satFacing = satAngle * (180 / Math.PI) - 90;
  // Signal pulse rings expand outward
  const signalPhase = (t % 2.5) / 2.5; // 0→1 every 2.5s
  // Star twinkling
  const starPhases = Array.from({ length: 30 }, (_, i) => stagger(i, 0.3));

  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <radialGradient id="so-earth" cx="45%" cy="40%" r="55%">
          <stop offset="0%" stopColor="#2196f3" /><stop offset="100%" stopColor="#0d47a1" />
        </radialGradient>
      </defs>
      <rect width="800" height="600" fill="#0a0a1e" rx="16" />
      {/* Stars — gentle twinkling */}
      {Array.from({ length: 30 }, (_, i) => {
        const baseOp = 0.3 + (i % 4) * 0.15;
        const twinkle = osc(t, 2.2 + (i % 6) * 0.35, baseOp * 0.4, baseOp, starPhases[i]);
        return (
          <circle key={i} cx={40 + (i * 257) % 720} cy={30 + (i * 191) % 540}
            r={0.8 + (i % 3) * 0.4} fill="#fff" opacity={twinkle} />
        );
      })}
      {/* Earth — slow self-rotation shimmer */}
      <circle cx="400" cy="300" r="100" fill="url(#so-earth)" />
      <path d="M340,260 Q380,240 400,270 Q420,300 380,310 Q350,300 340,260Z" fill="#2e7d32" opacity="0.5" />
      <path d="M420,260 Q460,250 470,280 Q475,310 440,300Z" fill="#2e7d32" opacity="0.4" />
      {/* Orbit path */}
      <ellipse cx="400" cy="300" rx="220" ry="80" fill="none" stroke="#546e7a" strokeWidth="1.5" strokeDasharray="8,4"
        transform="rotate(-20, 400, 300)" />
      {/* Satellite — continuously orbiting */}
      <g transform={`translate(${satX}, ${satY}) rotate(${satFacing})`}>
        {/* Body */}
        <rect x="-10" y="-8" width="20" height="16" rx="2" fill="#bdbdbd" stroke="#757575" strokeWidth="1" />
        {/* Solar panels */}
        <rect x="-40" y="-4" width="28" height="8" rx="1" fill={accentColor} opacity="0.85" />
        <rect x="12" y="-4" width="28" height="8" rx="1" fill={accentColor} opacity="0.85" />
        {/* Antenna */}
        <line x1="0" y1="-8" x2="0" y2="-18" stroke="#bdbdbd" strokeWidth="1.5" />
        <circle cx="0" cy="-20" r="3" fill="#e0e0e0" />
      </g>
      {/* Signal pulse expanding from satellite */}
      {[0, 0.33, 0.66].map((offset, i) => {
        const phase = ((signalPhase + offset) % 1);
        const r = phase * 60 + 8;
        const opacity = (1 - phase) * 0.4;
        return (
          <circle key={i} cx={satX} cy={satY} r={r} fill="none"
            stroke={accentColor} strokeWidth="1" opacity={opacity} />
        );
      })}
      <text x="400" y="560" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 3. Galaxy Spiral ────────────────────────────────────── */
export interface GalaxySpiralProps {
  label?: string;
  rotation?: number;
  /** Seconds since scene started — drives continuous galaxy rotation and star drift. */
  t?: number;
}
export const GalaxySpiral: React.FC<GalaxySpiralProps> = (props) => {
  const label = props.label || "Galaxy";
  const t = props.t ?? 0;

  // Slow galaxy rotation: one full turn every 20 seconds
  const galaxyRot = orbitAngle(t, 20.0) * (180 / Math.PI);
  // Core pulse
  const coreGlow = osc(t, 3.5, 14, 16);
  // Star brightness phases along arms (deterministic, no Math.random)
  const armStarSizes = [0.8, 1.1, 0.6, 1.3, 0.9, 1.0, 0.7, 1.2, 0.5, 1.0, 0.8, 0.6, 1.1, 0.9, 0.7];
  // Background star twinkle phases
  const bgStarPhases = Array.from({ length: 60 }, (_, i) => stagger(i, 0.2));

  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <radialGradient id="gx-core" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fff9c4" /><stop offset="40%" stopColor="#f9a825" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#f9a825" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="800" height="600" fill="#050510" rx="16" />
      {/* Background stars — slow twinkling */}
      {Array.from({ length: 60 }, (_, i) => {
        const baseOp = 0.2 + (i % 5) * 0.1;
        const twinkle = osc(t, 3.0 + (i % 7) * 0.3, baseOp * 0.4, baseOp, bgStarPhases[i]);
        return (
          <circle key={i} cx={30 + (i * 137) % 740} cy={20 + (i * 97) % 560}
            r={0.5 + (i % 4) * 0.3} fill="#fff" opacity={twinkle} />
        );
      })}
      {/* Entire galaxy rotates slowly */}
      <g transform={`rotate(${galaxyRot}, 400, 300)`}>
        {/* Spiral arms — three at 120° apart */}
        {[0, 120, 240].map((offset, armIdx) => (
          <g key={offset} transform={`rotate(${offset}, 400, 300)`}>
            <path d="M400,300 Q450,250 500,240 Q560,230 600,260 Q640,300 620,350"
              fill="none" stroke="#7c4dff" strokeWidth="30" opacity="0.15" strokeLinecap="round" />
            <path d="M400,300 Q450,250 500,240 Q560,230 600,260 Q640,300 620,350"
              fill="none" stroke="#b388ff" strokeWidth="6" opacity="0.3" strokeLinecap="round" />
            {/* Stars along arm — brightness oscillates with staggered phases */}
            {[1, 2, 3, 4, 5].map((s) => {
              const idx = armIdx * 5 + s - 1;
              const starBright = osc(t, 2.5 + s * 0.4, 0.3, 0.7, stagger(idx, 0.4));
              return (
                <circle key={s}
                  cx={400 + s * 45}
                  cy={300 - s * 10 + Math.sin(s) * 15}
                  r={armStarSizes[idx % armStarSizes.length]}
                  fill="#e1bee7" opacity={starBright} />
              );
            })}
          </g>
        ))}
        {/* Core glow — gentle pulse */}
        <circle cx="400" cy="300" r="60" fill="url(#gx-core)" />
        <circle cx="400" cy="300" r={coreGlow} fill="#fff9c4" opacity="0.85" />
      </g>
      <text x="400" y="560" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 4. Moon Phases ──────────────────────────────────────── */
export interface MoonPhasesProps {
  label?: string;
  activeIndex?: number;
  phases?: string[];
  /** Seconds since scene started — drives twinkling stars and moonlight shimmer. */
  t?: number;
}
export const MoonPhases: React.FC<MoonPhasesProps> = (props) => {
  const label = props.label || "Moon Phases";
  const activeIndex = props.activeIndex ?? -1;
  const phases = props.phases || ["New", "Waxing Crescent", "First Quarter", "Waxing Gibbous", "Full", "Waning Gibbous", "Last Quarter", "Waning Crescent"];
  const t = props.t ?? 0;

  // Active moon shimmer: pulse the highlight ring
  const activeGlow = osc(t, 2.2, 0.4, 0.9);
  // Full moon (index 4) gets a slow pulse
  const fullMoonR = 32 + osc(t, 3.0, -1, 1.5);
  // Star twinkle
  const starPhases = Array.from({ length: 30 }, (_, i) => stagger(i, 0.28));

  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0a0a1e" rx="16" />
      {/* Twinkling stars */}
      {Array.from({ length: 30 }, (_, i) => {
        const baseOp = 0.25 + (i % 4) * 0.1;
        const twinkle = osc(t, 2.8 + (i % 5) * 0.35, baseOp * 0.3, baseOp, starPhases[i]);
        return (
          <circle key={i} cx={40 + (i * 193) % 720} cy={30 + (i * 127) % 200}
            r={0.6} fill="#fff" opacity={twinkle} />
        );
      })}
      {/* 8 moon phases */}
      {phases.map((p, i) => {
        const x = 80 + i * 85;
        const y = 280;
        const r = i === 4 ? fullMoonR : 32;
        const shadowWidth = Math.abs(i - 4) * (r / 4);
        const isActive = i === activeIndex;
        return (
          <g key={i} opacity={isActive ? 1 : 0.7}>
            {/* Active highlight ring pulses */}
            {isActive && (
              <circle cx={x} cy={y} r={r + 6} fill="none" stroke="#fff" strokeWidth="1.5" opacity={activeGlow} />
            )}
            {/* Moon */}
            <circle cx={x} cy={y} r={r} fill="#e0e0e0"
              stroke={isActive ? "#fff" : "#546e7a"} strokeWidth={isActive ? 2.5 : 1} />
            {/* Shadow */}
            {i !== 4 && (
              <clipPath id={`mp-clip-${i}`}><circle cx={x} cy={y} r={r} /></clipPath>
            )}
            {i === 0 && <circle cx={x} cy={y} r={r} fill="#1a1a2e" />}
            {i > 0 && i < 4 && (
              <rect x={x + r - shadowWidth * 2} y={y - r} width={shadowWidth * 2} height={r * 2}
                fill="#1a1a2e" clipPath={`url(#mp-clip-${i})`} />
            )}
            {i > 4 && (
              <rect x={x - r} y={y - r} width={shadowWidth * 2} height={r * 2}
                fill="#1a1a2e" clipPath={`url(#mp-clip-${i})`} />
            )}
            {/* Label */}
            <text x={x} y={y + 55} textAnchor="middle" fill={isActive ? "#fff" : "#bdc3c7"}
              fontSize="10" fontWeight="500">{p}</text>
          </g>
        );
      })}
      <text x="400" y="440" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 5. Constellation Map ────────────────────────────────── */
export interface ConstellationMapProps {
  label?: string;
  revealCount?: number;
  /** Seconds since scene started — drives star twinkling and connection-line shimmer. */
  t?: number;
}
export const ConstellationMap: React.FC<ConstellationMapProps> = (props) => {
  const label = props.label || "Constellation";
  const revealCount = props.revealCount ?? 99;
  const t = props.t ?? 0;

  const stars = [
    { x: 200, y: 200 }, { x: 280, y: 180 }, { x: 360, y: 190 }, { x: 420, y: 220 },
    { x: 460, y: 300 }, { x: 540, y: 320 }, { x: 600, y: 280 },
  ];
  const edges = [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6]];

  // Per-constellation-star twinkle
  const constellationStarPhases = stars.map((_, i) => stagger(i, 0.45));
  // Background star twinkle
  const bgStarPhases = Array.from({ length: 50 }, (_, i) => stagger(i, 0.22));
  // Line shimmer
  const lineShimmer = osc(t, 4.0, 0.4, 0.7);

  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#050515" rx="16" />
      {/* Background stars — twinkling */}
      {Array.from({ length: 50 }, (_, i) => {
        const baseOp = 0.15 + (i % 5) * 0.05;
        const twinkle = osc(t, 3.2 + (i % 6) * 0.28, baseOp * 0.3, baseOp, bgStarPhases[i]);
        return (
          <circle key={i} cx={40 + (i * 163) % 720} cy={30 + (i * 109) % 540}
            r={0.5 + (i % 3) * 0.3} fill="#fff" opacity={twinkle} />
        );
      })}
      {/* Connection lines — shimmer in brightness */}
      {edges.map(([a, b], i) => (
        <line key={i} x1={stars[a].x} y1={stars[a].y} x2={stars[b].x} y2={stars[b].y}
          stroke="#7c4dff" strokeWidth="1.5"
          opacity={Math.min(a, b) < revealCount - 1 ? lineShimmer : 0.1} />
      ))}
      {/* Constellation stars — bright twinkling */}
      {stars.map((s, i) => {
        const twinkle = osc(t, 1.8 + (i % 3) * 0.5, 0.7, 1.0, constellationStarPhases[i]);
        const haloOp = osc(t, 2.4 + i * 0.3, 0.06, 0.18, constellationStarPhases[i]);
        return (
          <g key={i} opacity={i < revealCount ? twinkle : 0.15}>
            <circle cx={s.x} cy={s.y} r="5" fill="#fff" />
            <circle cx={s.x} cy={s.y} r="12" fill="#fff" opacity={haloOp} />
          </g>
        );
      })}
      <text x="400" y="500" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 6. Star Lifecycle ───────────────────────────────────── */
export interface StarLifecycleProps {
  label?: string;
  activeIndex?: number;
  stages?: string[];
  /** Seconds since scene started — drives star pulsing, glow breathing, arrow shimmer. */
  t?: number;
}
export const StarLifecycle: React.FC<StarLifecycleProps> = (props) => {
  const label = props.label || "Star Lifecycle";
  const activeIndex = props.activeIndex ?? -1;
  const stages = props.stages || ["Nebula", "Protostar", "Main Sequence", "Red Giant", "Supernova"];
  const t = props.t ?? 0;

  const stageData = [
    { x: 100, r: 35, c: "#7c4dff" },
    { x: 230, r: 30, c: "#ff9800" },
    { x: 380, r: 40, c: "#ffeb3b" },
    { x: 530, r: 55, c: "#e53935" },
    { x: 680, r: 25, c: "#fff" },
  ];
  // Each star breathes at its own pace (smaller = faster breath)
  const stagePeriods = [5.0, 4.2, 3.5, 6.0, 2.8];
  // Arrow visibility shimmer
  const arrowShimmer = osc(t, 3.5, 0.4, 0.7);

  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <marker id="sl-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0,0 L10,5 L0,10Z" fill="#546e7a" />
        </marker>
      </defs>
      <rect width="800" height="600" fill="#050515" rx="16" />
      {/* Stages along an arrow path */}
      {stageData.map((s, i) => {
        const isActive = i === activeIndex;
        // Pulsing radius — active stage pulses more visibly
        const amp = isActive ? 4 : 1.5;
        const rPulse = s.r + osc(t, stagePeriods[i], -amp, amp, stagger(i, 0.8));
        const glowOp = isActive
          ? osc(t, stagePeriods[i], 0.1, 0.25, stagger(i, 0.8))
          : osc(t, stagePeriods[i] * 1.2, 0.04, 0.1, stagger(i, 0.8));
        const nextX = i < 4 ? stageData[i + 1].x - stageData[i + 1].r - 5 : 0;
        return (
          <g key={i}>
            {/* Arrow between stages */}
            {i < 4 && (
              <line x1={s.x + s.r + 5} y1="280" x2={nextX} y2="280"
                stroke="#546e7a" strokeWidth="2" markerEnd="url(#sl-arrow)"
                opacity={arrowShimmer} />
            )}
            {/* Glow aura */}
            <circle cx={s.x} cy="280" r={rPulse + 15} fill={s.c} opacity={glowOp} />
            {/* Star */}
            <circle cx={s.x} cy="280" r={rPulse}
              fill={isActive ? "#fff" : s.c}
              opacity={isActive ? 1 : 0.75}
              stroke={isActive ? "#fff" : "none"} strokeWidth="2" />
            {/* Label */}
            <text x={s.x} y={280 + s.r + 30} textAnchor="middle"
              fill={isActive ? "#fff" : "#bdc3c7"} fontSize="12" fontWeight="500">
              {stages[i] || `Stage ${i + 1}`}
            </text>
          </g>
        );
      })}
      <text x="400" y="440" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 7. Telescope View ───────────────────────────────────── */
export interface TelescopeViewProps {
  label?: string;
  accentColor?: string;
  /** Seconds since scene started — drives star twinkling, nebula drift, planet slow orbit. */
  t?: number;
}
export const TelescopeView: React.FC<TelescopeViewProps> = (props) => {
  const label = props.label || "Through the Telescope";
  const accentColor = props.accentColor || "#7c4dff";
  const t = props.t ?? 0;

  // Planet slowly drifts across the view
  const planetDrift = wobble(t, 12.0, 18, Math.PI * 0.15);
  // Nebula breathes in opacity and shifts slightly
  const nebulaDrift = wobble(t, 8.0, 6);
  const nebulaOp = osc(t, 6.5, 0.09, 0.16);
  // Star twinkle phases
  const starPhases = Array.from({ length: 25 }, (_, i) => stagger(i, 0.3));

  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <radialGradient id="tv-vignette" cx="50%" cy="50%" r="50%">
          <stop offset="60%" stopColor="#0a0a1e" stopOpacity="0" />
          <stop offset="100%" stopColor="#000" stopOpacity="0.9" />
        </radialGradient>
        <clipPath id="tv-lens"><circle cx="400" cy="280" r="200" /></clipPath>
      </defs>
      <rect width="800" height="600" fill="#000" rx="16" />
      {/* Telescope view circle */}
      <g clipPath="url(#tv-lens)">
        <rect x="200" y="80" width="400" height="400" fill="#050520" />
        {/* Nebula — drifts gently */}
        <ellipse cx={350 + nebulaDrift} cy="260" rx="80" ry="60" fill={accentColor} opacity={nebulaOp} />
        <ellipse cx={420 - nebulaDrift * 0.5} cy="290" rx="60" ry="45" fill="#e91e63" opacity={nebulaOp * 0.65} />
        {/* Stars through telescope — twinkling */}
        {Array.from({ length: 25 }, (_, i) => {
          const baseOp = 0.4 + (i % 3) * 0.2;
          const twinkle = osc(t, 1.6 + (i % 5) * 0.4, baseOp * 0.4, baseOp, starPhases[i]);
          return (
            <circle key={i} cx={250 + (i * 73) % 300} cy={120 + (i * 53) % 320}
              r={0.8 + (i % 4) * 0.6} fill="#fff" opacity={twinkle} />
          );
        })}
        {/* Planet — slow drift */}
        <circle cx={440 + planetDrift} cy="240" r="18" fill="#e65100" opacity="0.7" />
        <ellipse cx={440 + planetDrift} cy="240" rx="30" ry="6" fill="none" stroke="#ffcc80" strokeWidth="2" opacity="0.5" />
      </g>
      {/* Vignette */}
      <circle cx="400" cy="280" r="200" fill="url(#tv-vignette)" />
      <circle cx="400" cy="280" r="200" fill="none" stroke="#1a1a1a" strokeWidth="8" />
      {/* Crosshair */}
      <line x1="400" y1="170" x2="400" y2="390" stroke="#546e7a" strokeWidth="0.5" opacity="0.5" />
      <line x1="290" y1="280" x2="510" y2="280" stroke="#546e7a" strokeWidth="0.5" opacity="0.5" />
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 8. Asteroid Belt ────────────────────────────────────── */
export interface AsteroidBeltProps {
  label?: string;
  rotation?: number;
  /** Seconds since scene started — drives continuous belt rotation and sun pulse. */
  t?: number;
}
export const AsteroidBelt: React.FC<AsteroidBeltProps> = (props) => {
  const label = props.label || "Asteroid Belt";
  const t = props.t ?? 0;

  // Belt rotates continuously: one revolution every 15 seconds
  const beltAngle = orbitAngle(t, 15.0) * (180 / Math.PI);
  // Sun pulsing
  const sunR = 40 + osc(t, 3.5, -2, 2);
  // Star twinkle
  const starPhases = Array.from({ length: 20 }, (_, i) => stagger(i, 0.32));
  // Inner planet orbits too
  const innerPlanetAngle = orbitAngle(t, 5.0, Math.PI * 0.7);
  const innerPlanetX = 400 + 120 * Math.cos(innerPlanetAngle);
  const innerPlanetY = 300 + 50 * Math.sin(innerPlanetAngle);

  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <radialGradient id="ab-sun" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fff9c4" /><stop offset="100%" stopColor="#f9a825" />
        </radialGradient>
      </defs>
      <rect width="800" height="600" fill="#050510" rx="16" />
      {/* Background stars — twinkling */}
      {Array.from({ length: 20 }, (_, i) => {
        const baseOp = 0.2;
        const twinkle = osc(t, 2.6 + (i % 5) * 0.3, baseOp * 0.3, baseOp * 1.5, starPhases[i]);
        return (
          <circle key={i} cx={50 + (i * 203) % 700} cy={30 + (i * 131) % 540}
            r={0.5} fill="#fff" opacity={twinkle} />
        );
      })}
      {/* Sun — gentle pulse */}
      <circle cx="400" cy="300" r={sunR + 15} fill="#f9a825" opacity="0.08" />
      <circle cx="400" cy="300" r={sunR} fill="url(#ab-sun)" />
      {/* Inner orbit */}
      <ellipse cx="400" cy="300" rx="120" ry="50" fill="none" stroke="#546e7a" strokeWidth="0.8"
        transform="rotate(-10, 400, 300)" />
      {/* Inner planet orbiting */}
      <circle cx={innerPlanetX} cy={innerPlanetY} r="6" fill="#3b82f6" opacity="0.8" />
      {/* Asteroid belt — continuously rotating */}
      <g transform={`rotate(${beltAngle}, 400, 300)`}>
        {Array.from({ length: 40 }, (_, i) => {
          const angle = (i * 9) * Math.PI / 180;
          const r = 180 + (i % 5) * 15;
          const ry = r * 0.35;
          // Subtle individual wobble for each asteroid
          const wobbleAmp = wobble(t, 3.0 + (i % 4) * 0.5, 1.5, stagger(i, 0.6));
          return (
            <ellipse key={i}
              cx={400 + Math.cos(angle) * (r + wobbleAmp)}
              cy={300 + Math.sin(angle) * ry}
              rx={2 + (i % 3)} ry={1.5 + (i % 2)}
              fill="#9e9e9e" opacity={0.4 + (i % 3) * 0.15}
              transform={`rotate(${i * 23}, ${400 + Math.cos(angle) * r}, ${300 + Math.sin(angle) * ry})`} />
          );
        })}
      </g>
      {/* Outer orbit */}
      <ellipse cx="400" cy="300" rx="300" ry="110" fill="none" stroke="#546e7a" strokeWidth="0.8"
        transform="rotate(-10, 400, 300)" />
      <text x="400" y="500" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};
